import {
  ADMIN_AVATAR_FALLBACK_BG,
  ADMIN_AVATAR_FALLBACK_TEXT,
} from '@/config/adminChartTheme';
import React, { useEffect, useState } from 'react';
import ChartCard from '../ChartCard';
import { fetchTopCreatorsByGmv } from '../../../services/campaignAnalyticsService';
import millify from 'millify';

interface TopCreator {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  photoPath: string;
  gmv: number;
  cashbackReceived: number;
  submissionsCount: number;
  campaignCount: number;
}

const RANK_STYLE = [
  'text-[#6C4EFF] font-bold',
  'text-[#8A72FF] font-semibold',
  'text-[#B1A0FF] font-semibold',
] as const;

interface TopCreatorsTableProps {
  refreshToken?: number;
}

const TopCreatorsTable: React.FC<TopCreatorsTableProps> = ({
  refreshToken,
}) => {
  const [data, setData] = useState<TopCreator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopCreatorsByGmv(5)
      .then((d) => setData(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  return (
    <ChartCard
      title="Top Creators"
      subtitle="By GMV generated"
      loading={loading}
    >
      {data.length > 0 ? (
        <div className="space-y-3">
          {data.map((c, i) => {
            const initials =
              `${c.firstName?.[0] ?? ''}${c.lastName?.[0] ?? ''}`.toUpperCase();
            return (
              <div
                key={c.userId}
                className="flex items-center gap-3 p-3 rounded-2xl hover:bg-[#FAFAFA] transition-colors"
              >
                <span
                  className={`w-6 text-center text-sm shrink-0 tabular-nums ${RANK_STYLE[i] ?? 'text-[#9B919D] font-medium'}`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div
                  className={`w-9 h-9 rounded-full overflow-hidden shrink-0 flex items-center justify-center ${ADMIN_AVATAR_FALLBACK_BG}`}
                >
                  {c.photoPath ? (
                    <img
                      src={c.photoPath}
                      alt={initials}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span
                      className={`text-xs font-bold ${ADMIN_AVATAR_FALLBACK_TEXT}`}
                    >
                      {initials || '?'}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#172B4D] truncate">
                    {c.firstName} {c.lastName}
                  </p>
                  <p className="text-[10px] text-[#9B919D]">
                    {c.campaignCount} campaigns · {c.submissionsCount}{' '}
                    submissions
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-[#172B4D]">
                    €{millify(c.gmv)}
                  </p>
                  <p className="text-[10px] text-[#9B919D]">
                    €{millify(c.cashbackReceived)} earned
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center">
          <p className="text-[#9B919D] text-sm">No creator data</p>
        </div>
      )}
    </ChartCard>
  );
};

export default TopCreatorsTable;
